import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faSave,
  faUndo,
  faGripVertical,
  faArrowUp,
  faArrowDown,
} from '@fortawesome/free-solid-svg-icons';

import getDraftDetails from '../../../../data/getDraftDetails';
import { setDraftDetails } from '../../../../data/setDraftDetails';
import { toastError, toastSuccess } from '../../../../helpers/toastHelpers';

const DraftOrderManager = () => {
  const [items, setItems] = useState([]);
  const [savedItems, setSavedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeDragIndex, setActiveDragIndex] = useState(null);
  const [activeOverIndex, setActiveOverIndex] = useState(null);

  const dragIndex = useRef(null);
  const dragOverIndex = useRef(null);

  const loadOrder = useCallback(async () => {
    setLoading(true);
    const result = await getDraftDetails();

    if (!result.success) {
      toastError('Unable to load draft order');
      setLoading(false);
      return;
    }

    const order = Array.isArray(result.draftOrder) ? result.draftOrder : [];
    setItems(order);
    setSavedItems(order);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  const handleDragStart = (index) => {
    dragIndex.current = index;
    dragOverIndex.current = index;
    setActiveDragIndex(index);
    setActiveOverIndex(index);
  };

  const handleDragOver = (event, index) => {
    event.preventDefault();
    dragOverIndex.current = index;
    setActiveOverIndex(index);
  };

  const clearDragState = () => {
    dragIndex.current = null;
    dragOverIndex.current = null;
    setActiveDragIndex(null);
    setActiveOverIndex(null);
  };

  const moveItem = (from, to) => {
    if (from === null || to === null || from === to || to < 0 || to >= items.length) return;

    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);

    setItems(next);
  };

  const handleDrop = () => {
    const from = dragIndex.current;
    const to = dragOverIndex.current;

    moveItem(from, to);
    clearDragState();
  };

  const handleDragEnd = () => {
    clearDragState();
  };

  const getIndexFromPoint = (x, y) => {
    const el = document.elementFromPoint(x, y);
    const listItem = el ? el.closest('[data-order-index]') : null;

    if (!listItem) return null;

    const index = Number.parseInt(listItem.getAttribute('data-order-index'), 10);
    return Number.isNaN(index) ? null : index;
  };

  const handleTouchStart = (event, index) => {
    if (!event.target.closest('[data-drag-handle="true"]')) return;

    event.preventDefault();

    dragIndex.current = index;
    dragOverIndex.current = index;
    setActiveDragIndex(index);
    setActiveOverIndex(index);
  };

  const handleTouchMove = (event) => {
    if (dragIndex.current === null) return;

    const touch = event.touches[0];
    if (!touch) return;

    event.preventDefault();
    const overIndex = getIndexFromPoint(touch.clientX, touch.clientY);
    if (overIndex === null) return;

    dragOverIndex.current = overIndex;
    setActiveOverIndex(overIndex);
  };

  const handleTouchEnd = () => {
    const from = dragIndex.current;
    const to = dragOverIndex.current;

    if (from !== null && to !== null) {
      moveItem(from, to);
    }

    clearDragState();
  };

  const saveOrder = async () => {
    setSaving(true);
    const result = await setDraftDetails({ draftOrder: items });

    if (result.success) {
      setSavedItems([...items]);
      toastSuccess('Draft order saved');
    } else {
      toastError(result.message || 'Unable to save draft order');
    }

    setSaving(false);
  };

  const resetOrder = () => {
    setItems([...savedItems]);
  };

  const isDirty = JSON.stringify(items) !== JSON.stringify(savedItems);

  if (loading) {
    return (
      <div className="text-center py-3">
        <FontAwesomeIcon icon={faSpinner} size="2x" spin className="text-yellow" />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <p className="mb-0 text-mute">No draft order entries found.</p>
    );
  }

  return (
    <div>
      <ol className="list-unstyled mb-3 draft-order-list" style={{ userSelect: 'none' }}>
        {items.map((name, index) => (
          <li
            key={`${name}-${index}`}
            data-order-index={index}
            draggable
            onDragStart={() => handleDragStart(index)}
            onDragOver={(event) => handleDragOver(event, index)}
            onDrop={handleDrop}
            onDragEnd={handleDragEnd}
            onTouchStart={(event) => handleTouchStart(event, index)}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchEnd}
            className={`draft-order-item d-flex align-items-center mb-2 p-2 shadow ${activeDragIndex === index ? 'draft-order-item--dragging' : ''} ${activeOverIndex === index ? 'draft-order-item--over' : ''}`}
            style={{ cursor: 'grab', borderRadius: 4 }}
          >
            <span className="draft-order-position mr-3 text-yellow-light text-bold" style={{ minWidth: 24 }}>
              {index + 1}.
            </span>
            <FontAwesomeIcon
              icon={faGripVertical}
              data-drag-handle="true"
              className="text-mute mr-3 draft-order-handle"
              onMouseDown={(event) => event.preventDefault()}
            />
            <span style={{ flexGrow: 1 }}>{name}</span>
            <div className="d-flex align-items-center ml-2">
              <button
                type="button"
                className="btn btn-link btn-sm p-1 text-yellow-light"
                onClick={() => moveItem(index, index - 1)}
                disabled={index === 0}
                aria-label={`Move ${name} up`}
                title="Move up"
              >
                <FontAwesomeIcon icon={faArrowUp} />
              </button>
              <button
                type="button"
                className="btn btn-link btn-sm p-1 text-yellow-light"
                onClick={() => moveItem(index, index + 1)}
                disabled={index === items.length - 1}
                aria-label={`Move ${name} down`}
                title="Move down"
              >
                <FontAwesomeIcon icon={faArrowDown} />
              </button>
            </div>
          </li>
        ))}
      </ol>

      <div className="d-flex align-items-center">
        <button
          type="button"
          className="btn btn-success btn-sm px-3 shadow mr-2"
          onClick={saveOrder}
          disabled={saving || !isDirty}
        >
          {saving
            ? <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
            : <FontAwesomeIcon icon={faSave} className="mr-2" />}
          Save Order
        </button>

        <button
          type="button"
          className="btn btn-outline-light btn-sm px-3 shadow"
          onClick={resetOrder}
          disabled={saving || !isDirty}
        >
          <FontAwesomeIcon icon={faUndo} className="mr-2" />
          Reset
        </button>
      </div>
    </div>
  );
};

export default DraftOrderManager;
